import {
  DuctType,
  type Coil as CoilType,
  type Damper as DamperType,
  type Fan as FanType,
  type Humidifier as HumidifierType,
} from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import { hasDuctReading, type DuctSensors } from "../helpers";
import { Position } from "../types";
import { Coil } from "./coil";
import { CoilDialog } from "./coil-dialog";
import { Damper } from "./damper";
import { DamperDialog } from "./damper-dialog";
import { Fan } from "./fan";
import { FanDialog } from "./fan-dialog";
import { Filter } from "./filter";
import { Humidifier } from "./humidifier";
import { HumidifierDialog } from "./humidifier-dialog";
import { Indicator } from "./indicator";
import { Outlet } from "./outlet";
import * as RecirculationDamper from "./recirculation-damper";
import { Sensors } from "./sensors";

interface SupplyProps {
  buildingId: string;
  airHandlerUnitId: string;
  position?: Position;
  temperature?: number;
  temperatureDefect?: boolean;
  setpoint?: number;
  actualSetpoint?: number;
  coolingSetpoint?: number;
  coolingActualSetpoint?: number;
  coils?: CoilType[];
  fan?: FanType;
  humidifier?: HumidifierType;
  dehumidifier?: HumidifierType;
  airFilterPressureSwitchDefect?: boolean;
  terminalFilterPressureSwitchDefect?: boolean;
  damper?: DamperType;
  sensors?: DuctSensors;
  pressureSwitchDefect?: boolean;
  recirculationDamper?: boolean;
}

const Supply: React.FC<SupplyProps> = ({
  buildingId,
  airHandlerUnitId,
  position = Position.TOP,
  temperature,
  temperatureDefect,
  setpoint,
  actualSetpoint,
  coolingSetpoint,
  coolingActualSetpoint,
  coils,
  fan,
  humidifier,
  dehumidifier,
  airFilterPressureSwitchDefect,
  terminalFilterPressureSwitchDefect,
  damper,
  sensors,
  pressureSwitchDefect,
  recirculationDamper,
}) => {
  return (
    <div
      className={cn(
        `gap-2.5 pl-2.5 after:right-16.25 after:h-23.5 relative flex flex-row items-center justify-end [grid-area:supply] *:shrink-0 after:absolute after:z-[-2] after:w-[calc(100%-65px)] after:border-t after:border-b after:border-t-primary-dark-gray after:border-b-primary-dark-gray after:bg-neutral-100 after:content-['']`,
        position === Position.TOP && "*:self-end after:-bottom-px",
        position === Position.BOTTOM && "*:self-start after:-top-px",
      )}
    >
      {recirculationDamper && <RecirculationDamper.Spacer />}
      {coils?.map(coil => (
        <CoilDialog key={coil.id} buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} coil={coil}>
          <Coil {...coil} position={position} />
        </CoilDialog>
      ))}
      {fan && (
        <FanDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} fan={fan}>
          <Fan {...fan} position={position} />
        </FanDialog>
      )}
      {humidifier && (
        <HumidifierDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} humidifier={humidifier}>
          <Humidifier {...humidifier} position={position} />
        </HumidifierDialog>
      )}
      {dehumidifier && (
        <HumidifierDialog
          buildingId={buildingId}
          airHandlerUnitId={airHandlerUnitId}
          humidifier={dehumidifier}
          dehumidifier
        >
          <Humidifier {...dehumidifier} position={position} />
        </HumidifierDialog>
      )}
      {airFilterPressureSwitchDefect !== undefined && (
        <Filter position={position} defect={airFilterPressureSwitchDefect} label={m.air_handler_unit_filter_supply()} />
      )}
      {damper && (
        <DamperDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} damper={damper}>
          <Damper {...damper} position={position} />
        </DamperDialog>
      )}
      {sensors && hasDuctReading(sensors) && (
        <Sensors buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} position={position} {...sensors} />
      )}
      {pressureSwitchDefect !== undefined && (
        <Indicator position={position} defect={pressureSwitchDefect} tooltip={m.pressure_switch()}>
          P
        </Indicator>
      )}
      <Outlet
        buildingId={buildingId}
        airHandlerUnitId={airHandlerUnitId}
        type={DuctType.SUPPLY}
        position={position}
        temperature={temperature}
        temperatureDefect={temperatureDefect}
        setpoint={setpoint}
        actualSetpoint={actualSetpoint}
        coolingSetpoint={coolingSetpoint}
        coolingActualSetpoint={coolingActualSetpoint}
        filter={
          terminalFilterPressureSwitchDefect !== undefined && (
            <Filter
              position={position}
              defect={terminalFilterPressureSwitchDefect}
              label={m.air_handler_unit_filter_supply_terminal()}
            />
          )
        }
      />
    </div>
  );
};

Supply.displayName = "Supply";

export { Supply };
export type { SupplyProps };
